import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  AISpecificHeadersSchema,
  GenericUserID,
  ResponseMetaSchema,
  ToolConfigDbSchema,
  ToolConfigSchema,
  URLParamsSchema,
} from '../models';

extendZodWithOpenApi(z);
const c = initContract();

/** Shared params (and a neat way to do optional params, but require at least one) */
const ToolConfigQuerySchema = z
  .object({
    toolId: URLParamsSchema.toolId
      .nullable()
      .optional()
      .transform(v => v || null),
    toolRaw: z.string().optional().nullable().openapi({
      example: 'github.com',
      description:
        "If we don't have a `tool.id` (e.g non-configured tool), tool_raw works as identifier",
    }),
  })
  .partial()
  .refine(data => !!data.toolId || !!data.toolRaw, 'Must pass either toolId or toolRaw');

export const apiToolsConfigs = c.router({
  getToolConfigs: {
    method: 'GET',
    path: `/1.0/tool/configs`,
    description: `Get the tool configurations for the authenticated user. One config per user per tool, linked to their personal account`,
    responses: {
      200: z.object({
        configs: z.array(ToolConfigDbSchema.or(ToolConfigSchema)),
        meta: ResponseMetaSchema,
      }),
    },
  },
  getDefaultToolConfigs: {
    method: 'GET',
    path: `/1.0/tool/configs/defaults`,
    description: `Get the default tool configurations for the authenticated user. One config per user per tool, linked to their personal account`,
    responses: {
      200: z.object({
        configs: z.array(ToolConfigDbSchema.or(ToolConfigSchema)),
        meta: ResponseMetaSchema,
      }),
    },
  },
  getToolConfigsForAi: {
    method: 'GET',
    path: `/1.0/tool/ai_configs`,
    query: z.object({ userId: GenericUserID }),
    description: 'Returns all tool configurations for the supplied (TIC) `user.id`.',
    responses: {
      200: z.object({
        configs: z.array(ToolConfigDbSchema.or(ToolConfigSchema)),
        meta: ResponseMetaSchema,
      }),
    },
    headers: AISpecificHeadersSchema,
  },
  getToolConfig: {
    method: 'GET',
    path: `/1.0/tool/config`,
    description: `Get the tool configuration for the authenticated user. One config per user per tool, linked to their personal account`,
    query: ToolConfigQuerySchema,
    responses: {
      200: ToolConfigDbSchema.or(ToolConfigSchema),
      204: z.null(),
    },
  },
  saveToolConfig: {
    method: 'PUT',
    path: `/1.0/tool/config`,
    description: `Create or update the tool configuration for the authenticated user. One config per user per tool, linked to their personal account`,
    query: ToolConfigQuerySchema,
    body: ToolConfigSchema,
    responses: {
      200: ToolConfigDbSchema,
      204: z.null(),
    },
  },
  deleteToolConfig: {
    method: 'DELETE',
    path: `/1.0/tool/config`,
    description: `Removes a tool configuration and returns the default configuration for that tool`,
    query: ToolConfigQuerySchema,
    body: c.noBody(),
    responses: {
      200: z.object({
        config: ToolConfigDbSchema.or(ToolConfigSchema).optional(),
        meta: z.object({
          is_predefined_config: z
            .boolean()
            .default(false)
            .describe('Indicates the `data` returned is a default/pre-defined config'),
        }),
      }),
    },
  },
  resetAllToolConfigs: {
    method: 'DELETE',
    path: `/1.0/tool/configs`,
    description: `Clear the user's customized tool configs, returning the default/pre-set configs via \`queryGetToolConfigs\``,
    responses: {
      200: z.object({
        configs: z.array(ToolConfigDbSchema.or(ToolConfigSchema)),
        meta: ResponseMetaSchema,
      }),
    },
  },
});
